import { useState } from 'react';
import { Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { LaneChip } from '../items/Tags';
import { cn } from '../../lib/utils';

/** (i) button next to a filter heading: how the values are defined, simple and exact. */
export default function FilterInfo({ definition, facetKey, className }) {
  const [open, setOpen] = useState(false);
  if (!definition) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn('rounded-full p-0.5 text-muted-foreground/70 hover:bg-accent hover:text-foreground', className)}
        aria-label={`How "${definition.title}" works`}
        title={`How "${definition.title}" works`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="block max-h-[85vh] max-w-2xl overflow-y-auto p-6">
          <DialogTitle className="text-lg font-semibold">{definition.title}</DialogTitle>
          <DialogDescription className="sr-only">How the {definition.title} values are defined</DialogDescription>
          <Tabs defaultValue="simple" className="mt-3">
            <TabsList>
              <TabsTrigger value="simple">In plain words</TabsTrigger>
              <TabsTrigger value="exact">Exact rules</TabsTrigger>
            </TabsList>
            <TabsContent value="simple" className="mt-4 space-y-4">
              <p className="text-sm leading-relaxed">{definition.plain}</p>
              <ul className="space-y-3">
                {definition.values.map((v) => (
                  <li key={v.value} className="rounded-lg border p-3">
                    <ValueName facetKey={facetKey} value={v.value} />
                    <p className="mt-1 text-sm leading-relaxed text-foreground/80">{v.plain}</p>
                    {v.how && <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">How: {v.how}</p>}
                  </li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="exact" className="mt-4 space-y-4">
              {definition.technical && <p className="text-sm leading-relaxed text-muted-foreground">{definition.technical}</p>}
              <ol className="space-y-2">
                {definition.values.map((v, i) => (
                  <li key={v.value} className="grid grid-cols-[1.5rem_1fr] gap-2 rounded-lg border p-3">
                    <span className="text-xs font-semibold tabular-nums text-muted-foreground">{i + 1}</span>
                    <div className="min-w-0">
                      <ValueName facetKey={facetKey} value={v.value} />
                      <p className="mt-1 font-mono text-[12px] leading-relaxed text-foreground/80">{v.rule}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="text-xs text-muted-foreground">Recalculated every night from Odoo, Shopify, Perfy and Google Ads data.</p>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ValueName({ facetKey, value }) {
  if (facetKey === 'exit_lane') return <LaneChip lane={value} />;
  return <span className="text-sm font-semibold">{value}</span>;
}
